import { useFormik } from 'formik';
import {
  ChangeEvent,
  FC,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  profilePasswordValidationSchema,
  profileValidationSchema,
} from 'src/utils/validationScheme.utils';
import Cookies from 'js-cookie';
import {
  apiUrls,
  dataStorageKeywords,
  errorMessages,
  help,
  successMessages,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import MobileField from 'src/components/InputFields/MobileField';
import TextField from 'src/components/InputFields/TextField';
import ImageUpload from 'src/components/InputFields/ImageUpload';
import {
  passwordInitialValues,
  userProfileInitialValues,
} from 'src/utils/initialValues.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import CustomBox from 'src/components/StyledComponents/StyledBox';
import { setEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  StyledSettingsContainer,
  StyledFormContainer,
  StyledSaveButton,
} from 'src/components/StyledComponents/StyledSettingsTab';
import {
  StyledHorizontalTabs,
  StyledHorizontalTab,
  TabPanel,
} from 'src/components/StyledComponents/StyledHorizontalTabs';
import { fetchUserProfileInfo } from 'src/utils/company.utils';
import { useUserProfile } from 'src/context/UserProfileContext';

export const ProfileTab: FC = () => {
  const { userProfile, refreshUserProfile } = useUserProfile();
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isValueChanged, setIsValueChanged] = useState(false);
  const [isPasswordValueChanged, setIsPasswordValueChanged] = useState(false);
  const [fetchedValues, setFetchedValues] = useState(userProfileInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { showSnackbar } = StyledSnackbar();

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formikPersonalInfo = useFormik({
    initialValues: userProfileInitialValues,
    validationSchema: profileValidationSchema,
    onSubmit: async () => {
      try {
        const payload: {
          firstName?: string;
          lastName?: string;
          mobileNo?: string;
        } = {};

        if (formikPersonalInfo.values.firstName !== fetchedValues.firstName) {
          payload.firstName = formikPersonalInfo.values.firstName;
        }

        if (formikPersonalInfo.values.lastName !== fetchedValues.lastName) {
          payload.lastName = formikPersonalInfo.values.lastName;
        }

        const currentMobileNo = `+${formikPersonalInfo.values.countryCode} ${formikPersonalInfo.values.phoneNumber}`;
        const originalMobileNo = fetchedValues.phoneNumber
          ? `+${fetchedValues.countryCode} ${fetchedValues.phoneNumber}`
          : '';

        if (currentMobileNo !== originalMobileNo) {
          payload.mobileNo = currentMobileNo;
        }

        const response = await axiosTokenInstance.patch(
          apiUrls.UPDATE_PROFILE_INFO,
          payload
        );

        const updatedFullName = `${response.data.user.firstName} ${response.data.user.lastName}`;
        setEncryptedLocalStorageData(
          dataStorageKeywords.FULL_NAME,
          updatedFullName
        );
        window.dispatchEvent(
          new StorageEvent('storage', { key: dataStorageKeywords.FULL_NAME })
        );

        formikPersonalInfo.setValues({
          ...formikPersonalInfo.values,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          phoneNumber: response.data.user.mobileNo.split(' ')[1] || '',
        });

        const fetchedData = {
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          countryCode:
            response.data.user.mobileNo.split(' ')[0].replace('+', '') || '94',
          phoneNumber: response.data.user.mobileNo.split(' ')[1] || '',
        };

        setFetchedValues(fetchedData);

        await refreshUserProfile();

        showSnackbar(response.data.message, 'success');
        setIsValueChanged(false);
      } catch (error: any) {
        if (
          error.response?.status === 400 ||
          error.response?.status === 403 ||
          error.response?.status === 409
        ) {
          showSnackbar(error.response.data.message, 'error');
        } else {
          showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
        }
      }
    },
  });

  const formikChangePassword = useFormik({
    initialValues: passwordInitialValues,
    validationSchema: profilePasswordValidationSchema,
    onSubmit: async (values) => {
      try {
        const response = await axiosTokenInstance.put(apiUrls.RESET_PASSWORD, {
          userId: Cookies.get(dataStorageKeywords.USER_ID),
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });

        showSnackbar(response.data.message, 'success');
        formikChangePassword.resetForm();
        setIsPasswordValueChanged(false);
      } catch (error: any) {
        if (error.response?.status === 401) {
          formikChangePassword.setFieldError(
            'newPassword',
            error.response.data.message
          );
        } else if (error.response?.status === 400) {
          formikChangePassword.setFieldError(
            'currentPassword',
            error.response.data.message
          );
        } else {
          showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
        }
      }
    },
  });

  useEffect(() => {
    loadUserInfo();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const isChanged =
        formikPersonalInfo.values.firstName !== fetchedValues.firstName ||
        formikPersonalInfo.values.lastName !== fetchedValues.lastName ||
        formikPersonalInfo.values.countryCode !== fetchedValues.countryCode ||
        formikPersonalInfo.values.phoneNumber !== fetchedValues.phoneNumber;

      setIsValueChanged(isChanged);
    }
  }, [formikPersonalInfo.values, isLoading, fetchedValues]);

  useEffect(() => {
    const isPasswordChanged =
      formikChangePassword.values.currentPassword !==
        passwordInitialValues.currentPassword ||
      formikChangePassword.values.newPassword !==
        passwordInitialValues.newPassword ||
      formikChangePassword.values.confirmPassword !==
        passwordInitialValues.confirmPassword;

    setIsPasswordValueChanged(isPasswordChanged);
  }, [formikChangePassword.values]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 2) {
        showSnackbar(help.FILE_SIZE_BIG, 'error');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      await handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axiosTokenInstance.put(
        apiUrls.UPLOAD_AVATAR,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      showSnackbar(successMessages.PROFILE_PICTURE_UPDATED, 'success');

      await refreshUserProfile();

      setImagePreview(null);

      window.dispatchEvent(
        new StorageEvent('storage', {
          key: dataStorageKeywords.AVATAR,
          newValue: response.data.avatarUrl,
        })
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || errorMessages.SOMETHING_WENT_WRONG;
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const loadUserInfo = async () => {
    setIsLoading(true);

    try {
      const data = userProfile || (await fetchUserProfileInfo());

      const profileData = {
        firstName: data.firstName,
        lastName: data.lastName,
        countryCode: data.countryCode,
        phoneNumber: data.phoneNumber,
      };

      formikPersonalInfo.setValues(profileData);
      setFetchedValues(profileData);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledSettingsContainer>
      <CustomBox height="220px" marginBottom={2} title="Avatar" width="auto">
        <ImageUpload
          imagePreview={imagePreview || userProfile?.avatarUrl || null}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
          onClick={handleUploadClick}
          buttonWidth="135px"
        />
      </CustomBox>

      <StyledHorizontalTabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="profile tabs"
      >
        <StyledHorizontalTab
          label="Personal Info"
          id="profile-tab-0"
          aria-controls="profile-tabpanel-0"
          selected={tabValue === 0}
        />
        <StyledHorizontalTab
          label="Reset Password"
          id="profile-tab-1"
          aria-controls="profile-tabpanel-1"
          selected={tabValue === 1}
        />
      </StyledHorizontalTabs>

      <TabPanel value={tabValue} index={0} idPrefix="profile-tabpanel">
        <CustomBox marginBottom={2} width="auto">
          <StyledFormContainer
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              formikPersonalInfo.handleSubmit();
            }}
            sx={{ width: '400px' }}
          >
            <TextField
              id="firstName"
              label="First Name"
              type="edit"
              formikProps={formikPersonalInfo}
            />
            <TextField
              id="lastName"
              label="Last Name"
              type="edit"
              formikProps={formikPersonalInfo}
            />

            <MobileField
              id="mobile"
              label="Mobile Number"
              formikProps={formikPersonalInfo}
            />

            <StyledSaveButton
              type="submit"
              isActive={isValueChanged}
              disabled={!isValueChanged}
            >
              Save
            </StyledSaveButton>
          </StyledFormContainer>
        </CustomBox>
      </TabPanel>

      <TabPanel value={tabValue} index={1} idPrefix="profile-tabpanel">
        <CustomBox marginBottom={2} width="auto">
          <StyledFormContainer
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              formikChangePassword.handleSubmit();
            }}
            sx={{ width: '400px' }}
          >
            <TextField
              id="currentPassword"
              label="Current Password"
              type="password"
              showPasswordToggle
              showPasswordColor="var(--loader-color)"
              formikProps={formikChangePassword}
              sx={{
                borderColor:
                  formikChangePassword.touched.currentPassword &&
                  formikChangePassword.errors.currentPassword
                    ? 'var(--error)'
                    : 'var(--border)',
              }}
            />
            <TextField
              id="newPassword"
              label="New Password"
              type="password"
              showPasswordToggle
              showPasswordColor="var(--loader-color)"
              formikProps={formikChangePassword}
              sx={{
                borderColor:
                  formikChangePassword.touched.newPassword &&
                  formikChangePassword.errors.newPassword
                    ? 'var(--error)'
                    : 'var(--border)',
              }}
            />
            <TextField
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              showPasswordToggle
              showPasswordColor="var(--loader-color)"
              formikProps={formikChangePassword}
              sx={{
                borderColor:
                  formikChangePassword.touched.confirmPassword &&
                  formikChangePassword.errors.confirmPassword
                    ? 'var(--error)'
                    : 'var(--border)',
              }}
            />
            <StyledSaveButton
              type="submit"
              isActive={isPasswordValueChanged}
              disabled={!isPasswordValueChanged}
            >
              Save
            </StyledSaveButton>
          </StyledFormContainer>
        </CustomBox>
      </TabPanel>
    </StyledSettingsContainer>
  );
};
